import React from "react";
import { StatusIndicator } from "../../../components/statusIndicator";

const ShiftReport = ({ reportData }) => {
  function IncidentSeverity(value) {
    switch (value.toLowerCase()) {
      case "low":
        return <StatusIndicator text="Low" type="success" />;
      case "medium":
        return <StatusIndicator text="Medium" type="warning" />;
      case "high":
        return <StatusIndicator text="High" type="danger" />;
      default:
        return;
    }
  }

  const formatDate = (value) =>
    new Date(value).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  const formatDateTime = (value) =>
    value
      ? new Date(value).toLocaleString(undefined, {
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : null;

  const formatDuration = (start, end) => {
    if (!start || !end) return null;
    const mins = Math.round(
      (new Date(end).getTime() - new Date(start).getTime()) / 60000
    );
    if (mins < 0) return null;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h ${m}m`;
  };

  return (
    <>
      <div className="pt-8 pb-6">
        <span className="text-sm block text-gray-800 dark:text-gray-100">
          Shift report for
        </span>
        <span className="text-xlg text-gray-800 dark:text-gray-100">
          {reportData.start_date !== reportData.end_date
            ? `${formatDate(reportData.start_date)} - ${formatDate(
                reportData.end_date
              )}`
            : formatDate(reportData.start_date)}
        </span>
      </div>
      <div>
        <span className="text-gray-700 dark:text-gray-300">Summary</span>
      </div>
      <div className="mt-2 grid grid-cols-3 lg:grid-cols-6 gap-4">
        {reportData.summary &&
          Object.entries(reportData.summary).map(([key, value]) => {
            const label = key.replace(/_/g, " ");
            return (
              <div key={key} className="bg-gray-50 dark:bg-gray-800 rounded-md p-4">
                <div className="text-xl font-bold text-gray-800 dark:text-gray-100">
                  {value}
                </div>
                <div className="text-xsm text-gray-600 dark:text-gray-400 capitalize">
                  {label}
                </div>
              </div>
            );
          })}
      </div>
      <div className="mt-8">
        {reportData.shifts &&
          reportData.shifts.map((shift) => {
            const sortedActivities = [...shift.activities].sort(
              (a, b) =>
                new Date(a.activity_date).getTime() -
                new Date(b.activity_date).getTime()
            );
            const clockIn = formatDateTime(shift.clock_in);
            const clockOut = formatDateTime(shift.clock_out);
            const duration = formatDuration(shift.clock_in, shift.clock_out);
            return (
              <div key={shift.shift_id} className="mb-8">
                {/* Shift header */}
                <div className="mb-2 flex items-baseline justify-between gap-3">
                  <div>
                    <h3 className="text-gray-700 dark:text-gray-200">
                      {shift.guard_name}
                      {shift.shift_name ? (
                        <span className="text-gray-500 dark:text-gray-400">
                          {" "}
                          · {shift.shift_name}
                        </span>
                      ) : null}
                    </h3>
                    <p className="text-xsm text-gray-700 dark:text-gray-400">
                      {clockIn || "Not clocked in"}
                      {" → "}
                      {clockOut || "Active"}
                      {duration ? ` · ${duration}` : ""}
                    </p>
                  </div>
                  {shift.is_ad_hoc ? (
                    <span className="shrink-0 text-xxsm uppercase tracking-wider rounded-md px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300">
                      Ad hoc
                    </span>
                  ) : null}
                </div>
                <div>
                  <table className="min-w-full max-w-full table-fixed">
                    <thead className="text-left text-xxsm text-black dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="font-medium px-3 py-3 rounded-tl-lg rounded-bl-lg w-20">
                          Time
                        </th>
                        <th className="font-medium px-3 py-3 w-40">Site</th>
                        <th className="font-medium px-3 py-3 w-48">
                          Activity type
                        </th>
                        <th className="font-semibold px-3 py-3 rounded-tr-lg rounded-br-lg">
                          Activity details
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-left">
                      {sortedActivities.length === 0 ? (
                        <tr>
                          <td
                            colSpan="4"
                            className="px-3 py-3 text-sm text-gray-400 dark:text-gray-500"
                          >
                            No activities logged during this shift
                          </td>
                        </tr>
                      ) : (
                        sortedActivities.map((activity) => (
                          <React.Fragment key={activity.activity_log_id}>
                            <tr>
                              <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 w-20">
                                {new Date(
                                  activity.activity_date
                                ).toLocaleTimeString(undefined, {
                                  hour: "2-digit",
                                  minute: "2-digit", hourCycle: "h23",
                                })}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 w-40">
                                {activity.site_name}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 w-48">
                                {Array.isArray(activity.activity_type)
                                  ? activity.activity_type.map((item, index) => (
                                      <span
                                        key={index}
                                        className={`block mr-1 ${
                                          index === 1 ? "text-xsm" : "text-sm"
                                        }`}
                                      >
                                        {item}
                                      </span>
                                    ))
                                  : activity.activity_type}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {Array.isArray(activity.activity_type) &&
                                activity.activity_type[0].toLowerCase() ===
                                  "incident report" ? (
                                  <div className="inline-block w-auto ml-2">
                                    {IncidentSeverity(activity.activity_detail)}
                                  </div>
                                ) : (
                                  activity.activity_detail
                                )}
                              </td>
                            </tr>
                            {activity.media_urls &&
                              activity.media_urls.trim() !== "" && (
                                <tr>
                                  <td
                                    colSpan="4"
                                    className="pt-0 px-3 pb-4 border-gray-100 dark:border-gray-800 border-b-2"
                                  >
                                    <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-3 gap-4 w-full">
                                      {activity.media_urls
                                        .split(",")
                                        .map((url, index) => (
                                          <img
                                            key={index}
                                            src={url}
                                            alt="Activity Media"
                                            className="w-full rounded shadow-sm"
                                          />
                                        ))}
                                    </div>
                                  </td>
                                </tr>
                              )}
                          </React.Fragment>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
      </div>
    </>
  );
};

export { ShiftReport };
